import { createEmbedder, FlatVectorStore } from '@fim/futurex-vector';
import { buildIndex, saveIndex, type Chunk } from '@fim/futurex-context';
import { findRepoRoot } from './repo.js';
import { indexPath, loadRetriever } from './index-store.js';
import { color, error, info, line } from './ui.js';

/** `futurex index` — scan the repo, chunk, embed (locally), extract AST symbols, persist. */
export async function runIndex(): Promise<void> {
  const root = findRepoRoot();
  const embedder = createEmbedder(); // DeepSeek-only: in-process local embedder
  const store = new FlatVectorStore<Chunk>();
  try {
    info(`indexing ${root} …`);
    const { stats, symbols, imports } = await buildIndex(root, embedder, store);
    saveIndex(indexPath(root), {
      embedderId: embedder.id,
      dimensions: embedder.dimensions,
      root,
      createdAt: new Date().toISOString(),
      records: store.toRecords(),
      symbols,
      imports,
    });
    info(
      `indexed ${stats.files} files → ${stats.chunks} chunks · ${stats.symbols} symbols · ${stats.imports} imports (${embedder.id})` +
        (stats.redacted > 0 ? ` · ${stats.redacted} secrets redacted` : '') +
        (stats.brokenImports > 0 ? ` · ${stats.brokenImports} broken imports` : ''),
    );
  } catch (err) {
    error(err instanceof Error ? err.message : String(err));
    process.exitCode = 1;
  }
}

/**
 * `futurex symbols [name]` — answer "is this symbol already defined?" from the AST
 * symbol index (§3.5). With no name, print index totals.
 */
export function runSymbols(name: string): void {
  const root = findRepoRoot();
  const retriever = loadRetriever(root);
  if (!retriever) {
    info('no index — run `futurex index` first');
    return;
  }
  const q = name.trim();
  const s = retriever.symbols.stats();
  if (!q) {
    info(`index: ${s.symbols} symbols across ${s.files} files`);
    return;
  }
  const defs = retriever.symbols.lookup(q);
  if (defs.length > 0) {
    line(`'${q}' is defined (${defs.length}):`);
    for (const d of defs) {
      const where = d.container ? `${d.container}.${d.name}` : d.name;
      line(`  ${d.kind} ${where} — ${d.file}:${d.startLine}${d.exported ? ' (exported)' : ''}`);
    }
    return;
  }
  const fuzzy = retriever.symbols.search(q, 10);
  if (fuzzy.length === 0) {
    line(`'${q}' is not defined in the index`);
    return;
  }
  line(`'${q}' is not defined. Similar:`);
  for (const d of fuzzy) line(`  ${d.name} (${d.kind}) — ${d.file}:${d.startLine}`);
}

/**
 * `futurex imports [file]` — show the import graph. No file → totals + broken
 * imports; a file → its imports (resolved / external / broken) and who imports it.
 */
export function runImports(file: string): void {
  const root = findRepoRoot();
  const retriever = loadRetriever(root);
  if (!retriever) {
    info('no index — run `futurex index` first');
    return;
  }
  const g = retriever.imports;
  const q = file.trim();
  if (!q) {
    const s = g.stats();
    info(
      `imports · ${s.edges} edges · ${s.internal} internal · ${s.external} external · ${s.broken} broken (${s.files} files)`,
    );
    const broken = g.broken();
    if (broken.length === 0) {
      line(color.green('no broken imports'));
      return;
    }
    line(color.red(`${broken.length} broken import${broken.length === 1 ? '' : 's'}:`));
    for (const b of broken.slice(0, 30)) line(`  ${b.file} ${color.dim('→')} ${b.specifier}`);
    if (broken.length > 30) line(color.dim(`  …and ${broken.length - 30} more`));
    return;
  }
  const want = q.split('\\').join('/');
  const target =
    g.importsOf(want).length > 0 || g.importersOf(want).length > 0
      ? want
      : g.files().find((f) => f === want || f.endsWith(`/${want}`) || f.endsWith(want));
  if (!target) {
    info(`no import data for '${want}' (not indexed, or it has no imports)`);
    return;
  }
  const imps = g.importsOf(target);
  const importers = g.importersOf(target);
  line(`${color.bold(target)} imports (${imps.length}):`);
  for (const i of imps) {
    const mark =
      i.kind === 'internal'
        ? color.green('→')
        : i.kind === 'external'
          ? color.dim('•')
          : color.red('✗ broken');
    line(`  ${mark} ${i.specifier}${i.resolved ? color.dim(` (${i.resolved})`) : ''}`);
  }
  if (importers.length > 0) {
    line(
      color.dim(
        `imported by ${importers.length}: ${importers.slice(0, 8).join(', ')}${importers.length > 8 ? ', …' : ''}`,
      ),
    );
  }
}
